// Piezas visuales compartidas del modulo RRHH (v49, spec §8).
// Panel Ver: etiqueta 10px gris mayuscula + valor 13px navy. Mismo lenguaje
// visual que el resto del programa (Proveedores, Gastos).

import React from 'react';
import { inputStyle } from '../utils/rrhh';

// Dato de solo lectura.
export function Dato({ label, valor, ancho }) {
  return (
    <div style={{ gridColumn: ancho ? `span ${ancho}` : undefined }}>
      <div style={{ fontSize: '10px', color: '#aaa', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </div>
      <div style={{ fontSize: '13px', color: '#012E40', marginTop: '2px', wordBreak: 'break-word' }}>
        {valor === null || valor === undefined || valor === '' ? <span style={{ color: '#ccc' }}>—</span> : valor}
      </div>
    </div>
  );
}

// Dato bloqueado: se muestra pero NO se edita aqui porque cambiarlo es una
// accion de personal (regla de oro, spec §5).
export function DatoBloqueado({ label, valor }) {
  return (
    <div>
      <div style={{ fontSize: '10px', color: '#aaa', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '5px' }}>
        {label}
        <span title="Solo cambia por accion de personal" style={{ color: '#B98B00', fontSize: '11px' }}>&#128274;</span>
      </div>
      <div style={{ fontSize: '13px', color: '#012E40', marginTop: '2px' }}>
        {valor === null || valor === undefined || valor === '' ? <span style={{ color: '#ccc' }}>—</span> : valor}
      </div>
    </div>
  );
}

// Campo de formulario. opciones = [{id, valor}] para select; sin opciones, input.
export function Campo({ label, valor, onChange, tipo = 'text', opciones = null, requerido = false, ancho, placeholder, deshabilitado = false }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', gridColumn: ancho ? `span ${ancho}` : undefined }}>
      <label style={{ fontSize: '10px', color: '#888', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}{requerido ? ' *' : ''}
      </label>
      {opciones
        ? (
          <select value={valor ?? ''} disabled={deshabilitado}
            onChange={e => onChange(e.target.value === '' ? null : e.target.value)}
            style={{ ...inputStyle, opacity: deshabilitado ? 0.6 : 1 }}>
            <option value="">— Seleccionar —</option>
            {opciones.map(o => <option key={o.id ?? o} value={o.id ?? o}>{o.valor ?? o}</option>)}
          </select>
        ) : (
          <input type={tipo} value={valor ?? ''} disabled={deshabilitado} placeholder={placeholder}
            onChange={e => onChange(e.target.value)}
            style={{ ...inputStyle, opacity: deshabilitado ? 0.6 : 1 }} />
        )}
    </div>
  );
}

// Indicador si/no (columnas BIT).
export function CampoCheck({ label, valor, onChange }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <label style={{ fontSize: '10px', color: '#888', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#555', padding: '8px 0' }}>
        <input type="checkbox" checked={!!valor} onChange={e => onChange(e.target.checked)} />
        {valor ? 'Sí' : 'No'}
      </label>
    </div>
  );
}

// Multiseleccion sobre una lista (tipos de licencia). valor = array de ids.
export function CampoMultiple({ label, valor = [], opciones = [], onChange }) {
  const alternar = (id) => {
    const actual = valor.map(String);
    onChange(actual.includes(String(id))
      ? actual.filter(x => x !== String(id))
      : [...actual, String(id)]);
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <label style={{ fontSize: '10px', color: '#888', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </label>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', padding: '6px 0' }}>
        {opciones.length === 0
          ? <span style={{ fontSize: 12, color: '#bbb' }}>Sin opciones en el catálogo.</span>
          : opciones.map(o => (
            <label key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, color: '#555' }}>
              <input type="checkbox" checked={valor.map(String).includes(String(o.id))} onChange={() => alternar(o.id)} />
              {o.valor}
            </label>
          ))}
      </div>
    </div>
  );
}

// Encabezado de seccion dentro de un formulario o panel.
export function Seccion({ titulo, children, columnas = 3 }) {
  return (
    <div style={{ marginBottom: '20px' }}>
      <div style={{ fontSize: '11px', fontWeight: '700', color: '#012E40', textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: '12px', paddingBottom: '6px', borderBottom: '2px solid #e8e8e8' }}>
        {titulo}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${columnas}, 1fr)`, gap: '12px' }}>
        {children}
      </div>
    </div>
  );
}

// Badge generico con paleta del proyecto.
export function Badge({ texto, tono = 'gris' }) {
  const tonos = {
    verde:   { bg: '#EAF3DE', color: '#3B6D11' },
    rojo:    { bg: '#FCEBEB', color: '#A32D2D' },
    azul:    { bg: '#e6f1fb', color: '#185FA5' },
    ambar:   { bg: '#FDF4E0', color: '#8A6100' },
    gris:    { bg: '#f0f0f0', color: '#888' },
    navy:    { bg: '#e8eef1', color: '#012E40' }
  };
  const t = tonos[tono] || tonos.gris;
  return (
    <span style={{ background: t.bg, color: t.color, padding: '2px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap' }}>
      {texto}
    </span>
  );
}

// Tono del badge segun el estado de una accion de personal.
export const tonoEstadoAccion = (estado) => ({
  'Aplicada': 'verde',
  'Rechazada': 'rojo',
  'Pendiente RRHH': 'ambar',
  'Pendiente Gerencia': 'ambar',
  'Borrador': 'gris'
}[estado] || 'gris');

// Tono segun estado laboral (A1). 'Laborando', no 'Activo': ese valor es del
// estado del expediente y significa otra cosa.
export const tonoEstadoLaboral = (estado) => ({
  'Laborando': 'verde',
  'Periodo de prueba': 'azul',
  'Preingreso': 'ambar',
  'Suspendido': 'rojo',
  'Terminado': 'gris'
}[estado] || 'gris');

// Banda de error/mensaje uniforme.
export function Aviso({ texto, tipo = 'error' }) {
  if (!texto) return null;
  const est = tipo === 'ok'
    ? { background: '#EAF3DE', border: '1px solid #b8dfc8', color: '#3B6D11' }
    : { background: '#FCEBEB', border: '1px solid #f5c6c6', color: '#A32D2D' };
  return (
    <div style={{ ...est, borderRadius: '8px', padding: '10px 14px', fontSize: '13px', marginBottom: '14px' }}>
      {texto}
    </div>
  );
}
